import React, { Component } from 'react';
import Header from '../Layout/Header';
import Footer2 from '../Layout/Footer2';
import { Helmet } from 'react-helmet';
import './JobScamDetector.css';

class JobScamDetector extends Component {
    constructor(props) {
        super(props);
        this.state = {
            jobDescription: '',
            analysis: null,
            isAnalyzing: false
        };
    }

    handleInputChange = (event) => {
        this.setState({ jobDescription: event.target.value });
    }

    analyzeJobDescription = () => {
        const { jobDescription } = this.state;
        
        if (!jobDescription.trim()) {
            alert('Please paste a job description to analyze');
            return;
        }

        this.setState({ isAnalyzing: true });

        // Simulate analysis delay
        setTimeout(() => {
            const analysis = this.performAnalysis(jobDescription);
            this.setState({ analysis, isAnalyzing: false });
        }, 1500);
    }

    performAnalysis = (text) => {
        const redFlags = [];
        const greenFlags = [];
        let legitimacyScore = 100;

        // Convert to lowercase for analysis
        const lowerText = text.toLowerCase();

        // Red flags detection
        const redFlagPatterns = [
            {
                pattern: /earn.*\$\d+.*week|make.*\$\d+.*day|guaranteed.*income/i,
                message: "Unrealistic income promises",
                severity: 30
            },
            {
                pattern: /work.*from.*home.*easy.*money|easy.*money.*guaranteed|no.*experience.*big.*money|simple.*work.*high.*pay|effortless.*income/i,
                message: "Too good to be true language",
                severity: 25
            },
            {
                pattern: /urgent.*hiring.*immediately|immediate.*start.*required|apply.*now.*limited|act.*fast|hurry.*limited|time.*sensitive.*opportunity/i,
                message: "Pressure tactics",
                severity: 20
            },
            {
                pattern: /pay.*upfront|registration.*fee|training.*fee.*required|starter.*kit.*fee|payment.*required.*training|fee.*before.*starting/i,
                message: "Upfront payment requests",
                severity: 40
            },
            {
                pattern: /email.*only|no.*phone|contact.*via.*email/i,
                message: "Limited contact methods",
                severity: 15
            },
            {
                pattern: /lottery|pyramid|mlm|multi.*level|network.*marketing/i,
                message: "MLM or pyramid scheme indicators",
                severity: 35
            },
            {
                pattern: /mystery.*shopper|reshipping|package.*forwarding/i,
                message: "Common scam job types",
                severity: 35
            },
            {
                pattern: /western.*union|bitcoin|cryptocurrency|wire.*transfer/i,
                message: "Suspicious payment methods",
                severity: 30
            }
        ];

        // Green flags detection
        const greenFlagPatterns = [
            {
                pattern: /benefits|health.*insurance|401k|retirement|vacation|pto/i,
                message: "Mentions benefits package"
            },
            {
                pattern: /company.*website|linkedin|glassdoor|established.*\d{4}|founded.*\d{4}|since.*\d{4}|our.*website|visit.*us|about.*us|our.*story|our.*mission/i,
                message: "Verifiable company information"
            },
            {
                pattern: /requirements|qualifications|experience.*required|skills/i,
                message: "Clear job requirements"
            },
            {
                pattern: /salary.*range|\$\d+.*\$\d+|competitive.*salary/i,
                message: "Transparent salary information"
            },
            {
                pattern: /interview.*process|background.*check|references/i,
                message: "Professional hiring process"
            },
            {
                pattern: /located.*in|office.*in|hybrid.*work|workspace.*requirements|dedicated.*workspace|professional.*environment/i,
                message: "Specific location or workspace requirements"
            }
        ];

        // Check for red flags
        redFlagPatterns.forEach(flag => {
            if (flag.pattern.test(lowerText)) {
                redFlags.push(flag.message);
                legitimacyScore -= flag.severity;
            }
        });

        // Check for green flags
        greenFlagPatterns.forEach(flag => {
            if (flag.pattern.test(lowerText)) {
                greenFlags.push(flag.message);
            }
        });

        // Additional checks
        if (text.length < 100) {
            redFlags.push("Job description is too short");
            legitimacyScore -= 20;
        }

        // Improved company identification check
        const companyIndicators = [
            /\b(company|employer|corporation|corp|inc|ltd|llc)\b/i,
            /we are|our (team|platform|mission|organization|company)/i,
            /founded|established|headquartered|based in/i,
            /join us|work with us|our culture|our values/i,
            /\b[A-Z][a-z]+\s+(is|was|has)\s+(founded|established|created)/i,
            /welcome to|about us|our story/i
        ];

        const hasCompanyIdentification = companyIndicators.some(pattern => 
            pattern.test(lowerText)
        ) || (text.length > 500 && /\b[A-Z][a-z]{2,}\b.*\b[A-Z][a-z]{2,}\b/.test(text));

        if (!hasCompanyIdentification) {
            redFlags.push("No clear company identification");
            legitimacyScore -= 15;
        }

        if (lowerText.split('!').length > 3) {
            redFlags.push("Excessive exclamation marks");
            legitimacyScore -= 10;
        }

        // Ensure score doesn't go below 0
        legitimacyScore = Math.max(0, legitimacyScore);

        let verdict = '';
        let verdictClass = '';

        if (legitimacyScore >= 80) {
            verdict = 'LIKELY LEGITIMATE';
            verdictClass = 'text-success';
        } else if (legitimacyScore >= 60) {
            verdict = 'PROCEED WITH CAUTION';
            verdictClass = 'text-warning';
        } else {
            verdict = 'LIKELY SCAM - AVOID';
            verdictClass = 'text-danger';
        }

        return {
            verdict,
            verdictClass,
            legitimacyScore,
            redFlags,
            greenFlags
        };
    }

    render() {
        const { jobDescription, analysis, isAnalyzing } = this.state;

        return (
            <div className="page-wraper">
                <Header />
                
                <Helmet>
                    <title>Job Scam Detector - Protect Yourself from Fraudulent Job Offers | OpenToWorkRemote</title>
                    <meta name="description" content="Free job scam detector tool. Analyze job descriptions instantly to identify red flags and protect yourself from fraudulent job offers. Stay safe while job hunting." />
                    <link rel="canonical" href="https://www.opentoworkremote.com/job-scam-detector" />
                </Helmet>

                <div className="page-content job-scam-detector">

                    {/* Main Content */}
                    <div className="section-full content-inner-2 bg-white">
                        <div className="container">
                            {/* Introduction */}
                            <div className="row m-b50">
                                <div className="col-lg-12 col-md-10 mx-auto text-center">
                                    <h1 className="h1 m-b20">Job Scam Detector</h1>
                                    <p className="lead m-b30">Paste any job description below and get an instant analysis to detect potential scams and red flags.</p>
                                </div>
                                <div className="card-body">
                                    <div className="form-group">
                                        <textarea
                                            id="jobDescription"
                                            className="form-control job-detector-textarea"
                                            rows="8"
                                            placeholder="Paste the job description here..."
                                            value={jobDescription}
                                            onChange={this.handleInputChange}
                                        />
                                    </div>
                                    
                                    <div className="text-center">
                                        <button
                                            className="detector-button button-lg"
                                            onClick={this.analyzeJobDescription}
                                            disabled={isAnalyzing}
                                        >
                                            {isAnalyzing ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm detector-mr-10" role="status" aria-hidden="true"></span>
                                                    Analyzing...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa fa-search detector-mr-10"></i>
                                                    Analyze Job Description
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {analysis && (
                                <div className="row mt-4">
                                    <div className="col-lg-12 mx-auto">
                                        <div className="analysis-results">
                                            <div className="verdict-display text-center">
                                                <div className="verdict-icon detector-mb-20">
                                                    <i className={`fa ${analysis.legitimacyScore >= 80 ? 'fa-check-circle' : 
                                                                    analysis.legitimacyScore >= 60 ? 'fa-exclamation-triangle' : 
                                                                    'fa-times-circle'}`}
                                                       style={{fontSize: '3rem', color: analysis.legitimacyScore >= 80 ? '#28a745' : 
                                                                                       analysis.legitimacyScore >= 60 ? '#ffc107' : '#dc3545'}}></i>
                                                </div>
                                                <h3 className={`verdict-title ${analysis.verdictClass}`}>
                                                    {analysis.verdict}
                                                </h3>
                                                <div className="progress legitimacy-progress detector-mt-20">
                                                    <div
                                                        className={`progress-bar ${
                                                            analysis.legitimacyScore >= 80 ? 'bg-success' :
                                                            analysis.legitimacyScore >= 60 ? 'bg-warning' : 'bg-danger'
                                                        }`}
                                                        role="progressbar"
                                                        style={{width: `${analysis.legitimacyScore}%`}}
                                                    >
                                                        Legitimacy Score: {analysis.legitimacyScore}%
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row detector-mt-30">
                                                {analysis.redFlags.length > 0 && (
                                                    <div className="col-lg-6 detector-mb-30">
                                                        <div className="alert-box danger-box">
                                                            <h5><i className="fa fa-exclamation-triangle"></i> Red Flags Detected:</h5>
                                                            <ul className="flag-list">
                                                                {analysis.redFlags.map((flag, index) => (
                                                                    <li key={index}>{flag}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                )}

                                                {analysis.greenFlags.length > 0 && (
                                                    <div className="col-lg-6 detector-mb-30">
                                                        <div className="alert-box success-box">
                                                            <h5><i className="fa fa-check-circle"></i> Positive Indicators:</h5>
                                                            <ul className="flag-list">
                                                                {analysis.greenFlags.map((flag, index) => (
                                                                    <li key={index}>{flag}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="tips-section detector-mt-30">
                                                <div className="tips-header text-center detector-mb-30">
                                                    <h4 className="h4" style={{color: 'white'}}>
                                                        <i className="fa fa-lightbulb-o detector-mr-10"></i>
                                                        General Tips to Avoid Job Scams
                                                    </h4>
                                                </div>
                                                <div className="row">
                                                    <div className="col-lg-8 mx-auto">
                                                        <ul className="tips-list">
                                                            <li><i className="fa fa-shield"></i> Never pay upfront fees for job opportunities</li>
                                                            <li><i className="fa fa-search"></i> Research the company thoroughly before applying</li>
                                                            <li><i className="fa fa-warning"></i> Be wary of jobs that seem too good to be true</li>
                                                            <li><i className="fa fa-phone"></i> Verify contact information and company details</li>
                                                            <li><i className="fa fa-heart"></i> Trust your instincts - if something feels off, it probably is</li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <Footer2 />
            </div>
        );
    }
}

export default JobScamDetector; 